<template>
  <span :class="badgeClasses">
    <span v-if="$slots['left-icon']" class="badge-icon badge-icon-left">
      <slot name="left-icon"></slot>
    </span>

    <div v-if="$slots.default" class="badge-content">
      <slot></slot>
    </div>

    <span v-if="$slots['right-icon']" class="badge-icon badge-icon-right">
      <slot name="right-icon"></slot>
    </span>
  </span>
</template>

<script>
import {badgeTypeValidator, fontWeightValidator} from '../../utils';

export default {
  name: 'UiBadge',
  props: {
    variant: {
      type: String,
      default: 'primary',
      validator: badgeTypeValidator
    },
    pill: {
      type: Boolean,
      default: false
    },
    size: {
      type: String,
      default: 'md',
      validator: value => ['sm', 'md', 'lg', 'xl'].includes(value)
    },
    position: {
      type: String,
      default: '',
      validator: value => ['top-right', 'top-left', 'bottom-right', 'bottom-left', ''].includes(value)
    },
    textTransform: {
      type: String,
      validator: value => ['lowercase', 'uppercase'].includes(value)
    },
    fontWeight: {
      type: String,
      default: 'semibold',
      validator: fontWeightValidator,
    }
  },
  computed: {
    badgeClasses() {
      return [
        'badge',
        `badge-${this.variant}`,
        { 'badge-pill': this.pill },
        'ui-badge',
        `ui-badge-${this.size}`,
        this.position ? `position-${this.position}` : '',
        this.textTransform ? `text-${this.textTransform}` : '',
        this.fontWeight ? `font-weight-${this.fontWeight}` : '',
      ];
    }
  }
};
</script>

<style>
/* Custom styles using the ui-badge class to override Bootstrap defaults */
.ui-badge.badge {
  display: inline-flex !important;
  gap: 4px;
  align-items: center;
  justify-content: center;
  font-size: 12px;
}

.ui-badge .badge-content {
  display: inline-flex;
  align-items: center;
}

.ui-badge .badge-icon {
  display: inline-flex;
  align-items: center;
}

.ui-badge.badge-success {
  background-color: var(--color-badge-success) !important;
  color: var(--color-green) !important;
}

.ui-badge.badge-promo {
  background-color: var(--color-badge-success-dark) !important;
}

.ui-badge.badge-recommended {
  border-color: var(--color-success-200) !important;
  background: var(--color-success-100) !important;
  color: var(--color-success-800) !important;
}

.ui-badge.badge-not-recommended {
  border-color: var(--color-warning-200) !important;
  background: var(--color-warning-100) !important;
  color: var(--color-warning-950) !important;
}

.ui-badge.badge-rejected {
  border-color: var(--color-error-200) !important;
  background: var(--color-error-100) !important;
  color: var(--color-error-800) !important;
}

.ui-badge.badge-primary {
  background-color: var(--color-primary) !important;
}
.ui-badge.badge-secondary {
  background-color: var(--color-secondary) !important;
}

.ui-badge.badge-inactive {
  background-color: var(--color-gray-100) !important;
  color: var(--color-gray-700) !important;
}

.ui-badge.badge-gradient {
  background: linear-gradient(92deg, var(--color-primary) 0%, var(--color-secondary) 100%);
}

.ui-badge.badge-pill {
  border-radius: 10rem;
}

.ui-badge.ui-badge-sm {
  font-size: 9px;
  font-weight: 600;
  line-height: 16px;
  gap: 2px;
}

.ui-badge.ui-badge-lg {
  font-size: 12px;
  font-weight: 600;
  line-height: 16px;
  gap: 2px;
}

.ui-badge.ui-badge-xl {
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
  gap: 2px;
}

.ui-badge.position-top-right {
  position: absolute;
  right: -1px;
  top: -5px;
  z-index: 999;
}
.ui-badge.position-top-left {
  position: absolute;
  left: 0px;
  top: -5px;
  z-index: 999;
}

.ui-badge.font-weight-light {
  font-weight: 400 !important;
}
</style>
